import React from 'react';
import LegalLayout, { Section } from '../components/LegalLayout';

const DPA = () => (
  <LegalLayout title="Data Processing Addendum" effectiveDate="February 1, 2026">
    <Section title="1. Parties And Scope">
      <p>
        This Data Processing Addendum ("DPA") forms part of the agreement between YOVAI LLC
        ("YOVAI", "Processor") and the client ("Controller") under which YOVAI provides its
        managed AI communication services. This DPA governs YOVAI's processing of personal
        information on behalf of Controller during the provision of the services.
      </p>
    </Section>

    <Section title="2. Definitions">
      <p>
        <strong>"Personal Information"</strong> means any information that relates to an
        identified or identifiable natural person that YOVAI processes on behalf of Controller.
      </p>
      <p>
        <strong>"Processing"</strong> means any operation performed on Personal Information,
        including collection, recording, storage, use, transmission, and deletion.
      </p>
      <p>
        <strong>"Sub-processor"</strong> means a third party engaged by YOVAI to process Personal
        Information on Controller's behalf as part of the services.
      </p>
    </Section>

    <Section title="3. Nature And Purpose Of Processing">
      <p>
        YOVAI processes Personal Information solely to provide the managed AI communication
        services, including answering inbound calls, capturing caller intent and details,
        scheduling or requesting appointments, sending confirmations and reminders, updating
        connected systems, and delivering internal notifications.
      </p>
      <p>
        Categories of data typically processed include caller name, phone number, email address,
        service or appointment request details, and the audio and transcript of the interaction.
      </p>
    </Section>

    <Section title="4. Controller Responsibilities">
      <p>
        Controller is responsible for the lawful basis on which Personal Information is collected
        and shared with YOVAI, including obtaining any required consents from callers and message
        recipients, providing accurate business knowledge, and defining the escalation and
        transfer rules used by YOVAI.
      </p>
    </Section>

    <Section title="5. Processor Responsibilities">
      <p>
        YOVAI will (a) process Personal Information only on documented instructions from
        Controller, including as configured in the deployment; (b) impose confidentiality
        obligations on personnel with access to Personal Information; (c) implement reasonable
        technical and organizational safeguards; and (d) assist Controller with data-subject
        requests and regulatory inquiries to the extent reasonably practicable.
      </p>
    </Section>

    <Section title="6. Security Measures">
      <p>
        YOVAI maintains administrative, technical, and physical safeguards designed to protect
        Personal Information against unauthorized access, disclosure, alteration, and loss. These
        include access controls, encryption of data in transit, vendor due diligence, and
        environment-level logging.
      </p>
    </Section>

    <Section title="7. Sub-Processors">
      <p>
        YOVAI relies on qualified Sub-processors to deliver components of the service, including
        cloud hosting, voice telephony, artificial intelligence model providers, calendaring
        systems, CRM systems, and messaging providers. YOVAI will impose data-protection
        obligations on Sub-processors that are no less protective than those in this DPA.
      </p>
      <p>
        A current list of Sub-processors is available on request by contacting
        <a href="mailto:yovel@yovaiagency.com"> yovel@yovaiagency.com</a>.
      </p>
    </Section>

    <Section title="8. Data-Subject Rights">
      <p>
        Where applicable law grants a data subject the right to access, correct, delete, restrict,
        object to, or port their Personal Information, YOVAI will provide reasonable assistance to
        Controller in responding to those requests.
      </p>
    </Section>

    <Section title="9. Retention And Deletion">
      <p>
        YOVAI retains Personal Information only for as long as necessary to provide the services
        or as required by law. Upon termination of the services, YOVAI will delete or return
        Personal Information at Controller's written request, subject to any legal retention
        obligation.
      </p>
    </Section>

    <Section title="10. Incident Notification">
      <p>
        If YOVAI becomes aware of a confirmed security incident affecting Personal Information
        processed on Controller's behalf, YOVAI will notify Controller without undue delay and
        provide the information reasonably available to assist Controller in meeting its own
        notification obligations.
      </p>
    </Section>

    <Section title="11. International Transfers">
      <p>
        Personal Information is processed in the United States. Where Controller directs the
        transfer of Personal Information from another jurisdiction, Controller is responsible for
        ensuring that a lawful transfer mechanism is in place.
      </p>
    </Section>

    <Section title="12. Term And Modification">
      <p>
        This DPA is effective for the duration of the underlying services agreement and any
        period during which YOVAI continues to process Personal Information on Controller's
        behalf. YOVAI may update this DPA to reflect changes in applicable law or the services;
        material changes will be communicated to Controller.
      </p>
    </Section>

    <Section title="13. Contact">
      <p>
        Questions about this DPA may be sent to
        <a href="mailto:yovel@yovaiagency.com"> yovel@yovaiagency.com</a>.
      </p>
    </Section>
  </LegalLayout>
);

export default DPA;
